import type { EncounterWorkDisposition } from '../../../types/encounterWork';

/**
 * IS THIS ENCOUNTER CURRENTLY HIDDEN? (GDN-3380, contract section 3.)
 *
 * A NON-ACTIVE DISPOSITION IS NOT THE SAME THING AS A HIDDEN ROW, and reading
 * it as one is the bug this function exists to prevent. Two states carry a
 * disposition and are nonetheless on screen:
 *
 *   - a SNOOZE THAT HAS EXPIRED — `state` is still `snoozed`, `hidden_until` is
 *     in the past, and the row is back in the queue (`returned_reason` =
 *     `snooze_expired`);
 *   - a RETURNED row — the payer moved or a corrected claim appeared after the
 *     disposition was written, so the server un-hid it (`returned_reason` =
 *     `lifecycle_event` or `new_claim`) while leaving the disposition in place
 *     as the record of what the biller decided.
 *
 * A kebab that treated either as hidden would offer only Restore, and a biller
 * looking at a claim that has come back to them could not archive or snooze it
 * again — exactly when they most want to.
 *
 * THE SERVER'S ANSWER WINS. The row's own `hidden` boolean is computed by the
 * full rule, which also compares the disposition's timestamp against the
 * family's latest lifecycle event and newest claim. This function cannot see
 * either, so it is the fallback for surfaces holding a disposition without a
 * row — the workbench, whose notes read returns a disposition and no row.
 */
export function isDispositionHiding(
  disposition: Pick<EncounterWorkDisposition, 'state' | 'hidden_until'> | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!disposition || disposition.state === 'active') return false;
  if (disposition.state === 'archived') return true;
  /* Snoozed: hiding only until the date passes. A null `hidden_until` on a
     snooze is a server that has not told us when it ends; treating that as
     hidden forever would strand the row, so it reads as visible. */
  if (!disposition.hidden_until) return false;
  const until = new Date(disposition.hidden_until);
  return !Number.isNaN(until.getTime()) && until > now;
}

/**
 * The row's `hidden` when there is a row, the disposition rule when there is
 * not. Kept as one function so no surface has to remember the precedence.
 */
export function resolveHidden(
  rowHidden: boolean | null | undefined,
  disposition: Pick<EncounterWorkDisposition, 'state' | 'hidden_until'> | null | undefined,
  now: Date = new Date(),
): boolean {
  return typeof rowHidden === 'boolean' ? rowHidden : isDispositionHiding(disposition, now);
}
